import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, MapPin, Flame, Users, PenLine, Sparkles, Radio, Clock, MessageSquare, ArrowBigUp } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHead, Pill, Progress } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1573152958734-1922c188fba3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxlbmVyZ2V0aWMlMjBjcm93ZCUyMGV2ZW50JTIwZGFya3xlbnwwfHx8fDE3ODYyMDQ5MTR8MA&ixlib=rb-4.1.0&q=85";
const COMMUNITY_IMG =
  "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjb21tdW5pdHklMjBnYXRoZXJpbmclMjBwZW9wbGUlMjBjb25uZWN0aW5nfGVufDB8fHx8MTc4NjIwNDkxNHww&ixlib=rb-4.1.0&q=85";

const fmtDate = (s?: string) =>
  s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

const Home = () => {
  const { user, login } = useAuth();
  const [events, setEvents] = useState<any[]>([]);
  const [petitions, setPetitions] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({ events: 0, petitions: 0, signatures: 0, members: 0 });
  const [heat, setHeat] = useState<any[]>([]);
  const [blogs, setBlogs] = useState<any[]>([]);
  const [threads, setThreads] = useState<any[]>([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/petitions").then((r) => setPetitions(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/energy/heatmap").then((r) => setHeat(r.data)).catch(() => {});
    api.get("/blogs").then((r) => setBlogs(r.data)).catch(() => {});
    api.get("/discussions", { params: { sort: "top" } }).then((r) => setThreads(r.data)).catch(() => {});
  }, []);

  const maxEnergy = Math.max(1, ...heat.map((h) => h.energy));

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Public gathering" className="h-full w-full object-cover opacity-30 grayscale" />
          <div className="absolute inset-0 bg-ink-base/70" />
          <div className="absolute inset-0 pc-grid-lines opacity-60" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10 pt-24 pb-20 lg:pt-32 lg:pb-28">
          <div className="max-w-4xl animate-rise">
            <div className="flex items-center gap-3">
              <span className="relative h-2 w-2 bg-neon">
                <span className="absolute inset-0 bg-neon animate-pulseGlow" />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.32em] text-neon">
                Public energy is live
              </span>
            </div>
            <h1 className="mt-6 font-bebas text-6xl sm:text-7xl lg:text-[104px] leading-[0.88] tracking-wide uppercase">
              Your voice is <span className="text-neon pc-text-glow">infrastructure</span>
              <br /> for the country.
            </h1>
            <p className="mt-7 max-w-2xl text-base text-white/60 leading-relaxed">
              People&apos;s Circle turns opinion into action — debate policy in the open, sign and launch
              petitions, show up at events, and back the people building things around you.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/events"
                data-testid="hero-events-cta"
                className="group bg-neon px-8 h-13 py-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow hover:brightness-110 transition-all duration-200"
              >
                Explore events <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </Link>
              {user ? (
                <Link
                  to="/petitions/new"
                  data-testid="hero-petition-cta"
                  className="border border-white/25 px-8 py-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.2em] hover:border-neon hover:text-neon transition-colors duration-200"
                >
                  Start a petition
                </Link>
              ) : (
                <button
                  onClick={login}
                  data-testid="hero-join-cta"
                  className="border border-white/25 px-8 py-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.2em] hover:border-neon hover:text-neon transition-colors duration-200"
                >
                  Join the circle
                </button>
              )}
            </div>
          </div>

          <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 border-t border-white/10">
            {[
              { k: "events", label: "Live events", icon: CalendarDays },
              { k: "petitions", label: "Active petitions", icon: PenLine },
              { k: "signatures", label: "Signatures", icon: Flame },
              { k: "discussions", label: "Open threads", icon: Radio },
              { k: "members", label: "Contributors", icon: Users },
            ].map(({ k, label, icon: Icon }) => (
              <div key={k} className="border-b border-r border-white/10 p-6" data-testid={`stat-${k}`}>
                <Icon size={16} className="text-neon" />
                <div className="mt-4 font-bebas text-5xl tracking-wide">{stats[k] ?? 0}</div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-white/40">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENTO: feed + events */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24">
        <SectionHead
          eyebrow="Happening first"
          title="Events near the action"
          action={
            <Link to="/events" data-testid="events-see-all" className="text-[12px] font-bold uppercase tracking-[0.2em] text-white/60 hover:text-neon transition-colors">
              See all events →
            </Link>
          }
        />
        {events.length === 0 ? (
          <div className="border border-dashed border-white/12 p-12 text-sm text-white/40" data-testid="home-events-empty">
            No approved events yet. Organizers can submit events from the dashboard.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-12">
            {events.slice(0, 5).map((ev, i) => (
              <Link
                key={ev.event_id}
                to={`/events/${ev.event_id}`}
                data-testid={`home-event-card-${i}`}
                className={`group relative overflow-hidden border border-white/10 bg-ink-surface hover:border-neon/50 hover:-translate-y-1 transition-[transform,border-color] duration-300 ${
                  i === 0 ? "md:col-span-7 md:row-span-2" : "md:col-span-5"
                }`}
              >
                <div className={`relative ${i === 0 ? "h-72" : "h-36"} overflow-hidden`}>
                  <img
                    src={ev.cover_image || HERO_IMG}
                    alt={ev.title}
                    className="h-full w-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-[opacity,transform] duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-surface via-ink-surface/20 to-transparent" />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <Pill tone="neon">{ev.is_paid ? "Ticketed" : "Free"}</Pill>
                    <Pill>{ev.category}</Pill>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className={`font-outfit font-bold ${i === 0 ? "text-2xl" : "text-lg"} leading-snug`}>{ev.title}</h3>
                  <p className="mt-2 text-sm text-white/50 line-clamp-2">{ev.summary}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-4 text-[12px] uppercase tracking-widest text-white/40">
                    <span className="flex items-center gap-1.5"><CalendarDays size={13} /> {fmtDate(ev.starts_at)}</span>
                    <span className="flex items-center gap-1.5"><MapPin size={13} /> {ev.city}</span>
                    <span className="flex items-center gap-1.5"><Users size={13} /> {ev.attendee_count} going</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* PUBLIC ENERGY */}
      <section className="border-y border-white/10 bg-ink-surface">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24">
          <SectionHead eyebrow="Heat map" title="Map of public energy" />
          <div className="grid gap-10 lg:grid-cols-12 items-start">
            <div className="lg:col-span-7 relative border border-white/10 bg-ink-base aspect-[4/3] overflow-hidden" data-testid="heatmap">
              <div className="absolute inset-0 pc-grid-lines opacity-70" />
              <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
                {heat.map((h, i) => {
                  const x = ((h.lng - 68) / (30)) * 100;
                  const y = 100 - ((h.lat - 6) / 30) * 100;
                  const r = 1.1 + (h.energy / maxEnergy) * 2.2;
                  return (
                    <g key={h.city}>
                      <circle cx={x} cy={y} r={r * 1.9} fill="#FF003C" opacity="0.14" className="animate-pulseGlow" style={{ animationDelay: `${i * 0.35}s` }} />
                      <circle cx={x} cy={y} r={r} fill="#FF003C" />
                      <text x={x + r + 1.5} y={y + 1} fontSize="2.4" fill="rgba(255,255,255,0.7)">{h.city}</text>
                    </g>
                  );
                })}
              </svg>
              {heat.length === 0 && (
                <div className="absolute inset-0 grid place-items-center text-sm text-white/35" data-testid="heatmap-empty">
                  Energy signals appear as events and petitions go live.
                </div>
              )}
            </div>
            <div className="lg:col-span-5">
              <p className="text-sm text-white/55 leading-relaxed">
                Every event booked and every petition signed lights up a city. This is where the
                country is loudest right now.
              </p>
              <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
                {heat.slice(0, 6).map((h) => (
                  <div key={h.city} className="py-4" data-testid={`heat-row-${h.city}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold">{h.city}</span>
                      <span className="text-white/40 text-xs uppercase tracking-widest">{h.energy} energy</span>
                    </div>
                    <div className="mt-3">
                      <Progress value={h.energy} goal={maxEnergy} />
                    </div>
                  </div>
                ))}
                {heat.length === 0 && <div className="py-6 text-sm text-white/35">No city data yet.</div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PETITIONS */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24">
        <SectionHead
          eyebrow="Pressure points"
          title="Petitions gaining force"
          action={
            <Link to="/petitions" data-testid="petitions-see-all" className="text-[12px] font-bold uppercase tracking-[0.2em] text-white/60 hover:text-neon transition-colors">
              See all petitions →
            </Link>
          }
        />
        {petitions.length === 0 ? (
          <div className="border border-dashed border-white/12 p-12 text-sm text-white/40" data-testid="home-petitions-empty">
            No approved petitions yet. Sign in and draft the first one.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {petitions.slice(0, 3).map((p, i) => (
              <Link
                key={p.petition_id}
                to={`/petitions/${p.petition_id}`}
                data-testid={`home-petition-card-${i}`}
                className="group border border-white/10 bg-ink-surface p-7 hover:border-neon/50 hover:-translate-y-1 transition-[transform,border-color] duration-300"
              >
                <Pill tone="blue">{p.category}</Pill>
                <h3 className="mt-5 font-outfit text-xl font-bold leading-snug group-hover:text-neon transition-colors">{p.title}</h3>
                <p className="mt-3 text-sm text-white/50 line-clamp-3">{p.summary}</p>
                <div className="mt-7">
                  <div className="flex items-end justify-between mb-3">
                    <span className="font-bebas text-3xl tracking-wide">{p.signature_count}</span>
                    <span className="text-[11px] uppercase tracking-[0.2em] text-white/40">of {p.goal} goal</span>
                  </div>
                  <Progress value={p.signature_count} goal={p.goal} />
                </div>
                <div className="mt-5 text-[11px] uppercase tracking-[0.18em] text-white/35">
                  Target · {p.target_authority}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* DISCUSSIONS + BLOGS */}
      <section className="border-y border-white/10 bg-ink-surface">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHead
              eyebrow="Open floor"
              title="Loudest threads"
              action={
                <Link to="/discussions" data-testid="discussions-see-all" className="text-[12px] font-bold uppercase tracking-[0.2em] text-white/60 hover:text-neon transition-colors">
                  All →
                </Link>
              }
            />
            <div className="divide-y divide-white/8 border-y border-white/10">
              {threads.slice(0, 4).map((t, i) => (
                <Link key={t.discussion_id} to={`/discussions/${t.discussion_id}`} data-testid={`home-thread-${i}`} className="group flex gap-5 py-5">
                  <span className="shrink-0 w-12 h-12 border border-white/10 grid place-items-center group-hover:border-neon group-hover:text-neon transition-colors">
                    <ArrowBigUp size={14} />
                    <span className="font-bebas text-sm tracking-wide">{t.upvotes}</span>
                  </span>
                  <div className="min-w-0">
                    <Pill tone="blue">{t.topic}</Pill>
                    <h4 className="mt-2 font-outfit font-semibold leading-snug group-hover:text-neon transition-colors">{t.title}</h4>
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-white/35">
                      <MessageSquare size={11} /> {t.reply_count} replies
                    </div>
                  </div>
                </Link>
              ))}
              {threads.length === 0 && <div className="py-6 text-sm text-white/35" data-testid="home-threads-empty">No threads yet.</div>}
            </div>
            <p className="mt-6 text-sm text-white/45">Public to read. Sign in to post.</p>
          </div>

          <div className="lg:col-span-7">
            <SectionHead
              eyebrow="Awareness"
              title="Reading room"
              action={
                <Link to="/blogs" data-testid="blogs-see-all" className="text-[12px] font-bold uppercase tracking-[0.2em] text-white/60 hover:text-neon transition-colors">
                  All →
                </Link>
              }
            />
            <div className="grid gap-6 sm:grid-cols-2">
              {blogs.slice(0, 4).map((b, i) => (
                <Link key={b.blog_id} to={`/blogs/${b.blog_id}`} data-testid={`home-blog-${i}`}
                  className="group border border-white/10 bg-ink-base p-6 hover:border-neon/50 hover:-translate-y-1 transition-[transform,border-color] duration-300">
                  <Pill>{b.category}</Pill>
                  <h4 className="mt-4 font-outfit font-bold leading-snug group-hover:text-neon transition-colors">{b.title}</h4>
                  <p className="mt-2 text-sm text-white/50 line-clamp-2">{b.summary}</p>
                  <div className="mt-5 flex items-center gap-4 text-[11px] uppercase tracking-[0.18em] text-white/35">
                    <span>{b.author_name}</span>
                    <span className="flex items-center gap-1.5"><Clock size={11} /> {b.read_minutes} min</span>
                  </div>
                </Link>
              ))}
              {blogs.length === 0 && <div className="text-sm text-white/35" data-testid="home-blogs-empty">No published pieces yet.</div>}
            </div>
            {user && (
              <Link to="/blogs/new" data-testid="home-submit-blog" className="mt-8 inline-flex items-center gap-2 border border-white/20 px-6 h-11 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
                <PenLine size={14} /> Submit a piece
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* SECTION SHELLS */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24">
        <SectionHead eyebrow="The rest of the circle" title="More ways to show up" />
        <div className="grid gap-6 md:grid-cols-12">
          <Link to="/discussions" data-testid="block-discussions" className="md:col-span-5 group border border-white/10 bg-ink-surface p-8 hover:border-neon/50 transition-colors duration-300">
            <Radio className="text-neon" size={18} />
            <h3 className="mt-5 font-bebas text-3xl tracking-wide uppercase">Discussions</h3>
            <p className="mt-3 text-sm text-white/50">Read anything publicly. Sign in to post. Topic-wise threads on policy that matters.</p>
          </Link>
          <Link to="/blogs" data-testid="block-blogs" className="md:col-span-7 group relative overflow-hidden border border-white/10 bg-ink-surface p-8 hover:border-neon/50 transition-colors duration-300">
            <img src={COMMUNITY_IMG} alt="Community" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale group-hover:opacity-30 transition-opacity duration-500" />
            <div className="relative">
              <Sparkles className="text-pcyellow" size={18} />
              <h3 className="mt-5 font-bebas text-3xl tracking-wide uppercase">Blogs & Awareness</h3>
              <p className="mt-3 max-w-md text-sm text-white/60">Community writes, admin curates. Long-form explainers on the issues under debate.</p>
            </div>
          </Link>
          <Link to="/spotlight" data-testid="block-spotlight" className="md:col-span-7 group border border-white/10 bg-ink-surface p-8 hover:border-neon/50 transition-colors duration-300">
            <Flame className="text-neon" size={18} />
            <h3 className="mt-5 font-bebas text-3xl tracking-wide uppercase">Spotlight & Support</h3>
            <p className="mt-3 max-w-md text-sm text-white/50">Startups, local business, sportspeople, researchers, creators — discover them and send a connect request.</p>
          </Link>
          <Link to="/fellowship" data-testid="block-fellowship" className="md:col-span-5 group border border-white/10 bg-ink-surface p-8 hover:border-pcblue/60 transition-colors duration-300">
            <Users className="text-pcblue" size={18} />
            <h3 className="mt-5 font-bebas text-3xl tracking-wide uppercase">Youth Fellowship</h3>
            <p className="mt-3 text-sm text-white/50">Become an ambassador for your city and run the circle on the ground.</p>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
